export type TargetingMode = 'location' | 'map';

export type GeoRadiusValue = {
    lat: number;
    lng: number;
    radiusKm: number;
};

export type GeographicLocation = {
    country: string | null;
    state: string | null;
    city: string | null;
    lat: number | null;
    lng: number | null;
    radiusKm: number | null;
};

type CountryOption = {
    iso2: string;
    name: string;
};

type CampaignGeoFields = {
    country?: string | null;
    state?: string | null;
    city?: string | null;
    geoRadius?: GeoRadiusValue | null;
};

const US_COUNTRY_ISO2 = 'US';

export const defaultRadiusKm = 10;

export const emptyGeographicLocation = (): GeographicLocation => ({
    country: null,
    state: null,
    city: null,
    lat: null,
    lng: null,
    radiusKm: null,
});

export const isRadiusLocation = (location?: Partial<GeographicLocation> | null): location is GeographicLocation & { lat: number; lng: number } =>
    typeof location?.lat === 'number' && typeof location?.lng === 'number';

export const buildGeographicLocation = (
    values: CampaignGeoFields,
    isGlobal: boolean,
    targetingMode: TargetingMode
): GeographicLocation => {
    if (isGlobal) {
        return emptyGeographicLocation();
    }

    if (targetingMode === 'map') {
        return {
            country: null,
            state: null,
            city: null,
            lat: values.geoRadius?.lat ?? null,
            lng: values.geoRadius?.lng ?? null,
            radiusKm: values.geoRadius?.radiusKm ?? null,
        };
    }

    return {
        country: values.country || null,
        state: values.country === US_COUNTRY_ISO2 ? values.state || null : null,
        city: values.city || null,
        lat: null,
        lng: null,
        radiusKm: null,
    };
};

export const getMinimumClickRate = (
    country?: string | null,
    state?: string | null,
    city?: string | null,
    geoRadius?: GeoRadiusValue | null
) => {
    if (geoRadius) {
        return 0.075;
    }

    if (country && (state || city)) {
        return 0.1;
    }

    if (country) {
        return 0.075;
    }

    return 0.05;
};

export const validateClickRate = (
    value: number | undefined,
    country?: string | null,
    state?: string | null,
    city?: string | null,
    geoRadius?: GeoRadiusValue | null
) => {
    const minimumRate = getMinimumClickRate(country, state, city, geoRadius);

    if (value && value < minimumRate) {
        return Promise.reject(`Minimum click rate should be at least $${minimumRate}`);
    }

    return Promise.resolve();
};

export const resolveCampaignGeoFormValues = (
    location: Partial<GeographicLocation> | undefined,
    countries: CountryOption[]
) => {
    const hasGeoRadius = isRadiusLocation(location);
    const hasCountryTarget = !!location?.country;
    const countryObj = location?.country
        ? countries.find(c => c.name === location.country || c.iso2 === location.country)
        : null;
    const countryValue = countryObj?.iso2 || location?.country || null;

    return {
        isGlobal: !hasGeoRadius && !hasCountryTarget,
        targetingMode: hasGeoRadius ? 'map' as TargetingMode : 'location' as TargetingMode,
        countryObj,
        countryValue,
        stateValue: location?.state || null,
        cityValue: location?.city || null,
        geoRadius: hasGeoRadius ? {
            lat: location.lat as number,
            lng: location.lng as number,
            radiusKm: location.radiusKm || defaultRadiusKm,
        } : null,
    };
};

export const formatGeographicLocationLabel = (location?: Partial<GeographicLocation> | null) => {
    if (!location?.country && !location?.state && !location?.city) {
        return 'Global';
    }

    return [location?.city, location?.state, location?.country].filter(Boolean).join(', ');
};
